import {DropdownOptionProps} from 'wix-ui-tpa/cssVars'
import {
  Area,
  ReservationLocation,
} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import {TFunction} from '@wix/yoshi-flow-editor'

export const ANY_AREA_ID = '__any_area__'

export type AreaOption = {
  id: string
  name: string
  description?: string
  isAnyArea?: boolean
}

export const getActiveAreaIds = (reservationLocation?: ReservationLocation) => {
  const tableDefinitions =
    reservationLocation?.configuration?.tableManagement?.tableDefinitions ?? []

  return new Set(
    tableDefinitions
      .filter(({isActive}) => isActive !== false)
      .map(({areaId}) => areaId)
      .filter((areaId): areaId is string => Boolean(areaId)),
  )
}

export const getVisibleAreas = (reservationLocation?: ReservationLocation): Area[] => {
  const areaConfiguration = reservationLocation?.configuration?.areaConfiguration

  if (!areaConfiguration?.enabled || !areaConfiguration.visibleToGuests) {
    return []
  }

  const activeAreaIds = getActiveAreaIds(reservationLocation)

  return (areaConfiguration.areas ?? []).filter(
    ({id, name}) => Boolean(id && name) && activeAreaIds.has(id!),
  )
}

export const getAreaOptions = (
  reservationLocation: ReservationLocation | undefined,
  t: TFunction,
): AreaOption[] => {
  const visibleAreas = getVisibleAreas(reservationLocation)

  if (visibleAreas.length <= 1) {
    return []
  }

  return [
    {
      id: ANY_AREA_ID,
      name: t('uou-reservations.shared.any-area'),
      isAnyArea: true,
    },
    ...visibleAreas.map(({id, name, description}) => ({
      id: id!,
      name: name!,
      description: description ?? undefined,
    })),
  ]
}

export const getAreaDropdownOptions = (areaOptions: AreaOption[]): DropdownOptionProps[] =>
  areaOptions.map(({id, name, description}) => ({
    id,
    value: name,
    subtitle: description,
    isSelectable: true,
  }))

export const normalizeAreaId = (areaId?: string) =>
  areaId && areaId !== ANY_AREA_ID ? areaId : undefined

export const getSelectedAreaOption = (
  areaOptions: AreaOption[],
  selectedAreaId?: string,
): AreaOption | undefined => {
  const normalizedAreaId = normalizeAreaId(selectedAreaId)

  return (
    areaOptions.find(({id}) => id === normalizedAreaId) ??
    areaOptions.find(({isAnyArea}) => isAnyArea) ??
    areaOptions[0]
  )
}

export const getAreaNameById = (
  reservationLocation: ReservationLocation | undefined,
  areaId?: string | null,
) =>
  reservationLocation?.configuration?.areaConfiguration?.areas?.find(({id}) => id === areaId)?.name

export const getReservationSummaryLocationText = ({
  reservationLocation,
  areaId,
  t,
}: {
  reservationLocation?: ReservationLocation
  areaId?: string | null
  t: TFunction
}) => {
  const locationName = reservationLocation?.location?.name
  const areaOptions = getAreaOptions(reservationLocation, t)
  const shouldShowArea = Boolean(areaOptions.length)
  const areaName = areaId ? getAreaNameById(reservationLocation, areaId) : undefined
  const areaLabel = shouldShowArea ? areaName ?? t('uou-reservations.shared.any-area') : undefined

  return [locationName, areaLabel].filter(Boolean).join(', ')
}
